import { formatKstStamp } from "@rma-orbit/shared";
import type { SessionUser } from "@rma-orbit/shared";

import { api } from "../api/client.js";
import { navigate } from "../App.js";
import { Busy, ErrorNotice, Notice, useAsync } from "../lib/ui.js";

/**
 * 대시보드 (harness/UI-SPEC.md §1).
 *
 * ★ 장기 체류 경고·승인 지연 경고는 **만들지 않는다** (UI-SPEC §3, 결정 C5).
 * 숫자만 보여주고 판단은 사람이 한다.
 */

export function Dashboard({ can, user }: { can: (p: string) => boolean; user: SessionUser }) {
  // 숫자는 서버가 세 준다. 화면이 items.length 로 세면 limit 에서 잘린 수를 전체인 양
  // 보여준다 — "SECUI 체류 200" 인데 실제로는 340 일 수 있다. 그래서 limit=1 만 받는다.
  const office = useAsync(() => api.cases({ status: "INSPECTING", limit: 1 }), []);
  const atVendor = useAsync(() => api.cases({ status: "AT_VENDOR", limit: 1 }), []);
  const received = useAsync(() => api.cases({ status: "RECEIVED", limit: 1 }), []);
  const ready = useAsync(() => api.cases({ status: "READY_TO_RETURN", limit: 1 }), []);
  // ★ SECUI 에 가 있는데 아직 교체 시리얼이 안 붙은 건 = 이 프로젝트의 작업 큐
  const awaitingSwap = useAsync(
    () => api.cases({ status: "AT_VENDOR", swap: "none", limit: 1 }),
    [],
  );
  const pendingSwaps = useAsync(() => api.swaps({ pending_only: true }), []);
  // 승인 대기 = 승인도 출고도 아직 없는 건 (HARNESS.md §6 - 표시만 한다)
  const approvalPending = useAsync(
    () => api.cases({ approval_pending: 1, limit: 1 }),
    [],
  );

  const cards = [
    { label: "입고 접수", icon: "⇩", data: received, hash: "#/cases", hint: "아직 검수 전" },
    { label: "검수 중", icon: "⌕", data: office, hash: "#/cases", hint: "사무실 보유" },
    { label: "SECUI 체류", icon: "⇄", data: atVendor, hash: "#/cases", hint: "이관 후 회수 대기" },
    { label: "반송 준비", icon: "⇧", data: ready, hash: "#/cases", hint: "협력사로 나갈 준비" },
    {
      label: "★ 교체 대기",
      icon: "★",
      data: awaitingSwap,
      hash: "#/cases?swap=none",
      hint: "SECUI 체류 중 교체 시리얼 미확인",
    },
    {
      label: "승인 대기",
      icon: "✓",
      data: approvalPending,
      hash: "#/cases",
      // 승인이 **아직 없는** 건이다. 승인은 났는데 출고만 안 한 건은 출고 대기이고,
      // 두 개를 한 줄에 섞으면 큐가 큐가 아니게 된다 (db/cases.ts 의 같은 주석)
      hint: "부장님 확인을 아직 못 받은 건",
    },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 11 ? "좋은 아침입니다" : hour < 17 ? "좋은 오후입니다" : "수고 많으셨습니다";
  const today = new Intl.DateTimeFormat("ko-KR", { dateStyle: "full", timeZone: "Asia/Seoul" }).format(new Date());

  const anyError = cards.find((c) => c.data.error)?.data.error;

  return (
    <>
      <div className="hero">
        <span className="brand-mark big" aria-hidden="true" />
        <div>
          <h1>
            {greeting}, {user.name}
          </h1>
          <p>지금 어디에 몇 대가 있는지만 봅니다. 지연 경고는 두지 않습니다.</p>
        </div>
        <div className="hero-right">
          <strong>{today}</strong>
          방화벽 RMA 관제 · Triz MS
        </div>
      </div>

      <ErrorNotice error={anyError} />

      <div className="row" style={{ alignItems: "stretch" }}>
        {cards.map((card) => (
          <button
            key={card.label}
            className={`card stat ${card.label.startsWith("★") ? "accent" : ""}`}
            onClick={() => navigate(card.hash)}
          >
            <div className="stat-label">
              <span className="navicon" aria-hidden="true">{card.icon}</span>
              {card.label}
            </div>
            <div className="stat-value">
              {card.data.loading ? "…" : (card.data.data?.total ?? 0)}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {card.hint}
            </div>
          </button>
        ))}
      </div>

      {/*
        사람 확인 대기 매칭 (UI-SPEC §6). 0건이 아니면 노랑 — 기한 경고는 없다.
        결정적 매칭이라 사실상 늘 0건이어야 하고, 0건이 아니면 그 자체가 신호다.
      */}
      {(pendingSwaps.data?.items.length ?? 0) > 0 && (
        <Notice kind="warn">
          <strong>사람이 확인해야 할 교체 매칭 {pendingSwaps.data!.items.length}건</strong> — 이
          프로젝트의 매칭은 결정적이라 보통 0건입니다. 0건이 아니라는 것 자체가 볼 이유입니다.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {pendingSwaps.data!.items.map((swap) => (
              <li key={swap.id}>
                <span className="mono">{swap.original_serial}</span> →{" "}
                <span className="mono">{swap.replacement_serial}</span> (확신도 {swap.confidence},
                근거 {swap.evidence_ref}) ·{" "}
                <a
                  href={`#/devices/${encodeURIComponent(swap.original_serial)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`#/devices/${encodeURIComponent(swap.original_serial)}`);
                  }}
                >
                  장비 이력 보기
                </a>
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {can("admin.users") && <HarnessWidget />}

      <div className="card">
        <h2>지금 할 일</h2>
        {received.loading ? (
          <Busy />
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              <a href="#/cases" onClick={(e) => { e.preventDefault(); navigate("#/cases"); }}>
                케이스 화면
              </a>
              의 <strong>[입고 접수]</strong> 에 시리얼을 붙여넣습니다. 시리얼 뒤에{" "}
              <strong>탭이나 공백 두 칸</strong>을 두고 증상을 적으면 같이 저장됩니다 — 엑셀의
              시리얼·비고 두 열을 그대로 복사해 붙여넣어도 됩니다.
            </li>
            <li>
              검수 결과와 콘솔 로그는 케이스 상세에서 적습니다. 로그는 <strong>[증적]</strong> 에
              붙여넣으면 원문 그대로 보관되고 증상 보고 메일에 함께 나갑니다.
            </li>
            <li>
              보낼 건을 체크박스로 골라 <strong>[증상 보고 메일]</strong> → 본문 복사 →
              그룹웨어에 붙여넣어 부장님께 보냅니다.
            </li>
            <li>확인받으면 같은 화면에서 [일괄 승인] 한 번, 그다음 송장을 등록합니다.</li>
            <li>
              출고 후 <strong>[송장 보고 메일]</strong> 로 한 번 더 보고합니다.
            </li>
            <li>
              ★ SECUI 에서 교체품 시리얼을 확인하면 케이스 상세의 <strong>[교체 시리얼 등록]</strong>
              에 적습니다 — 위 <strong>[★ 교체 대기]</strong> 가 그 대기 줄입니다.
            </li>
          </ul>
        )}
      </div>

    </>
  );
}


/** 24시간. HARNESS.md §6 - 이 시간을 넘겨 안 돌렸으면 빨강이다 */
const STALE_MS = 24 * 60 * 60 * 1000;

/**
 * 하네스 판정 상시 표시 (HARNESS.md §6).
 *
 * 기준은 `site-verify` 다 — 코드를 고칠 때마다 돌려야 하는 검사이고, 그것이
 * 낡았다는 것은 **지금 화면에 보이는 것이 검사된 적 없는 코드**라는 뜻이다.
 * 판정이 없으면 통과로 채우지 않고 없다고 말한다(SOUL S2). 나머지 7종은 설정 화면.
 */
function HarnessWidget() {
  const runs = useAsync(() => api.harness(), []);
  if (runs.loading || runs.error) return null; // 조용히 빠진다 - 대시보드를 막지 않는다

  const site = (runs.data ?? []).find((r) => r.harness === "site-verify");
  const failing = (runs.data ?? []).filter((r) => r.verdict === "ISSUES_FOUND");

  if (site === undefined) {
    return (
      <Notice kind="warn">
        <strong>하네스를 돌린 기록이 없습니다.</strong> 로컬에서 <code>pnpm harness:all</code> 을
        돌리면 판정이 여기와{" "}
        <a href="#/system" onClick={(e) => { e.preventDefault(); navigate("#/system"); }}>
          설정 화면
        </a>
        에 남습니다 — 돌린 적 없는 것을 통과로 채우지는 않습니다.
      </Notice>
    );
  }

  const ranMs = Date.parse(site.run_at);
  const stale = !Number.isNaN(ranMs) && Date.now() - ranMs > STALE_MS;
  const bad = site.verdict === "ISSUES_FOUND" || failing.length > 0 || stale;

  return (
    <Notice kind={bad ? "error" : "info"}>
      <strong>
        site-verify {site.verdict}
        {failing.length > 0 && ` · 실패한 검사 ${failing.length}종`}
      </strong>{" "}
      · 마지막 실행 {formatKstStamp(site.run_at, true)}
      {stale && " — 24시간이 넘었습니다"}
      {". "}
      <a href="#/system" onClick={(e) => { e.preventDefault(); navigate("#/system"); }}>
        8종 전부 보기
      </a>
    </Notice>
  );
}
